/**
 * Offline detection for save / mutation failures.
 *
 * The browser doesn't give us a single reliable "you're offline" signal:
 *
 *   - `navigator.onLine` flips to false when the OS reports no connectivity,
 *     but it stays `true` when only one specific endpoint is unreachable
 *     (proxy down, DNS failure, etc).
 *   - `fetch()` rejects with a `TypeError("Failed to fetch")` for both true
 *     network failures and CORS / DNS issues.
 *
 * We treat anything that smells like a transport-level problem as offline.
 * The cost of a false positive is small: we'd hold the queue and retry on
 * the user's next action. The cost of a false negative is bigger — marking
 * the user stale on a transient blip — so we lean toward retry-friendly.
 *
 * Conditions explicitly NOT treated as offline:
 *   - HTTP 4xx/5xx responses (those are server-side; ConflictError → stale,
 *     anything else → unrecoverable, also stale).
 *   - AbortError (request was cancelled deliberately).
 */
export function isOfflineError(err: unknown): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true
  if (err instanceof TypeError) {
    return /fetch|network|networkerror|load failed/i.test(err.message)
  }
  return false
}
